import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Check, Heart } from "lucide-react";
import { toast } from "sonner";
import { useLang, L } from "@/i18n";
import { api, formatApiError } from "@/api";
import { SectionHead, Reveal } from "@/components/Widgets";

export default function Survey() {
  const { lang, t } = useLang();
  const [searchParams] = useSearchParams();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState("");
  const [consent, setConsent] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/survey").then((r) => setQuestions(r.data)).catch(() => {});
  }, []);

  const setMulti = (qid, opt) => {
    setAnswers((prev) => {
      const cur = prev[qid] || [];
      return { ...prev, [qid]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/survey/responses", {
        answers, contact: contact.trim(), consent, fonte: searchParams.get("fonte") || "",
      });
      setSuggestions(data.suggestions || []);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24" data-testid="survey-thanks">
        <motion.div className="text-center" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
          <span className="inline-flex w-20 h-20 rounded-full bg-terracotta items-center justify-center mb-6">
            <Heart className="w-9 h-9 text-white fill-white" />
          </span>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-grafite">{t("survey.thanks")}</h1>
          <p className="mt-4 font-accent text-2xl text-mare">{t("survey.thanksSub")}</p>
        </motion.div>
        {suggestions.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading font-bold text-2xl text-grafite mb-6 text-center" data-testid="survey-suggestions">{t("survey.suggestions")}</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {suggestions.map((s) => (
                <Link key={s.slug} to={`/scheda/${s.slug}`} data-testid={`suggestion-${s.slug}`}
                  className="card-lift block bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(15,76,131,0.08)]">
                  {s.images?.[0] && <img src={s.images[0]} alt="" className="w-full h-28 object-cover" loading="lazy" />}
                  <div className="p-4">
                    <p className="font-heading font-semibold text-grafite leading-snug">{L(s.title, lang)}</p>
                    <p className="text-xs text-grafite/60 mt-1 line-clamp-2">{L(s.excerpt, lang)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="survey-page">
      <SectionHead kicker="Follonica Like" title={t("survey.title")} sub={t("survey.sub")} />
      <form onSubmit={submit} className="flex flex-col gap-6">
        {questions.map((q, qi) => (
          <Reveal key={q.id} delay={qi * 0.05}>
            <fieldset className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(14,110,140,0.08)]" data-testid={`survey-q-${q.id}`}>
              <legend className="font-heading font-semibold text-lg text-grafite">
                {qi + 1}. {L(q.question, lang)}
                {q.kind === "multi" && <span className="block text-xs font-body font-normal text-grafite/50 mt-1">({t("survey.multi")})</span>}
              </legend>
              <div className="mt-4">
                {q.kind === "rating" && (
                  <div className="flex gap-2" role="radiogroup">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button type="button" key={n} data-testid={`rating-${q.id}-${n}`} onClick={() => setAnswers((p) => ({ ...p, [q.id]: n }))}
                        className="transition-transform hover:scale-110" aria-label={`${n}/5`} aria-pressed={answers[q.id] >= n}>
                        <Star className={`w-9 h-9 ${(answers[q.id] || 0) >= n ? "fill-terracotta text-terracotta" : "text-grafite/25"}`} />
                      </button>
                    ))}
                  </div>
                )}
                {q.kind === "multi" && (
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((o) => {
                      const on = (answers[q.id] || []).includes(o.id);
                      return (
                        <button type="button" key={o.id} data-testid={`opt-${q.id}-${o.id}`} onClick={() => setMulti(q.id, o.id)} aria-pressed={on}
                          className={`rounded-full px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 transition-colors ${on ? "bg-mare text-white" : "bg-sabbia/60 text-grafite hover:bg-sabbia"}`}>
                          {on && <Check className="w-3.5 h-3.5" />} {L(o.label, lang)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {q.kind === "profile" && (
                  <div className="flex flex-wrap gap-2">
                    <p className="w-full text-xs text-grafite/50 mb-1">({t("survey.profileHint")})</p>
                    {q.options.map((o) => {
                      const on = answers[q.id] === o.id;
                      return (
                        <button type="button" key={o.id} data-testid={`opt-${q.id}-${o.id}`}
                          onClick={() => setAnswers((p) => ({ ...p, [q.id]: o.id }))} aria-pressed={on}
                          className={`rounded-full px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 transition-colors ${on ? "bg-terracotta text-white" : "bg-sabbia/60 text-grafite hover:bg-sabbia"}`}>
                          {on && <Check className="w-3.5 h-3.5" />} {L(o.label, lang)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {q.kind === "text" && (
                  <input
                    data-testid={`text-${q.id}`}
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value }))}
                    className="w-full rounded-2xl border border-black/10 bg-calce px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-turchese"
                    placeholder="…"
                  />
                )}
              </div>
            </fieldset>
          </Reveal>
        ))}
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(14,110,140,0.08)]" data-testid="survey-contact-block">
          <p className="font-heading font-semibold text-lg text-grafite">{t("survey.contact")}</p>
          <input
            data-testid="survey-contact"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            className="mt-3 w-full rounded-2xl border border-black/10 bg-calce px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-turchese"
            placeholder="nome@email.com · +39 …"
          />
          {contact.trim() && (
            <label className="mt-3 flex items-start gap-3 text-sm text-grafite/80 cursor-pointer">
              <input type="checkbox" data-testid="survey-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#EF7C00]" />
              {t("survey.consent")}
            </label>
          )}
        </div>
        <button type="submit" data-testid="survey-submit" disabled={sending}
          className="rounded-full bg-terracotta hover:bg-[#d15f2d] disabled:opacity-60 text-white font-bold px-8 py-4 text-base transition-colors self-center">
          {sending ? t("survey.sending") : t("survey.submit")}
        </button>
      </form>
    </div>
  );
}
